import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

/**
 * The Trade Feed's "Hide groups" list.
 *
 * The hidden set belongs to the STORE, not to this browser: it is saved
 * server-side and the server drops those groups from every feed query itself
 * (TradeFeedGroups / TradeFeedService::withExcludedGroups). So nothing here is
 * sent with the feed request — `version` only tells the feed to reload once a
 * change has actually been saved. Reloading on the optimistic tick instead
 * would race the save and fetch the old exclusions.
 */

const GROUPS_URL = "/api/trade-feed/groups";
const VISIBILITY_URL = "/api/trade-feed/groups/visibility";

export default function useHiddenGroups() {
  const [groups, setGroups] = useState([]);
  const [version, setVersion] = useState(0);

  const load = useCallback(
    () =>
      axios
        .get(GROUPS_URL, { metadata: { silent: true } })
        .then((response) => {
          if (response?.data?.items) setGroups(response.data.items);
        })
        .catch(() => {
          // The picker is a convenience; the feed works without it.
        }),
    [],
  );

  useEffect(() => {
    load();
  }, [load]);

  /** Hide (or show) these groups for everyone in the store. */
  const setHidden = useCallback(
    (names, hidden) => {
      if (!names.length) return;

      // Optimistic, so the tick lands under the cursor immediately.
      setGroups((prev) => prev.map((g) => (names.includes(g.name) ? { ...g, hidden } : g)));

      axios
        .post(VISIBILITY_URL, { groups: names, hidden })
        .then((response) => {
          // A 5xx RESOLVES undefined here (global interceptor), so no items
          // means the save failed: re-read the truth rather than keep a tick
          // the server never stored.
          if (!response?.data?.items) return load();
          setGroups(response.data.items);
          setVersion((v) => v + 1);
          return undefined;
        })
        .catch(() => load());
    },
    [load],
  );

  const hidden = useMemo(() => groups.filter((g) => g.hidden).map((g) => g.name), [groups]);

  return { groups, hidden, setHidden, version };
}
